// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { layoutContainer, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    confirmGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        cssOverrides: {
            scrim: css`
                ${xScreen("lte", "sm")} {
                    height: 180px;
                }
                ${xScreen("lte", "xs")} {
                    height: 200px;
                }
                @media print {
                    display: none;
                }
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                width: 100%;
                padding: 15px 0;
            `,
            drawerBody: css`
                box-shadow: 0px 100px 150px;
                pointer-events: all;
                height: 125px;
                ${xScreen("lte", "sm")} {
                    height: 180px;
                }
                ${xScreen("lte", "xs")} {
                    height: 200px;
                }
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            ${layoutContainer}
            >div {
                justify-content: space-between;
            }
        `,
    },
    messageGridItem: {
        width: [12, 12, 7, 9, 9],
    },
    confirmGridItem: {
        width: [12, 12, 3, 2, 2],
        css: css`
            display: flex;
            flex-flow: column;
            align-items: center;
            ${xScreen("lte", "sm")} {
                flex-flow: row;
                justify-content: space-between;
                padding-top: 15px;

                button {
                    width: auto;
                }
            }
        `,
    },
    acceptButton: {
        mergeCss: true,
        css: css`
            padding: 0 10px;
            width: 100%;
            margin-bottom: 10px;
        `,
    },
    closeGridItem: {
        width: [4, 3, 2, 1, 1],
    },
    closeButton: {
        variant: "secondary",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "sm")} {
                margin-bottom: 10px;
            }
        `,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const { location, isMobileScreen } = useAppSelector(state => ({
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        location: getLocation(state),
    }));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 365 });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    // XNG-Changes: Update structure
    return (
        <Drawer
            isOpen={isOpen}
            {...styles.drawer}
            size={isMobileScreen ? 120 : 110}
            data-test-selector="drawer_CookiePolicy"
        >
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                        {siteMessage("Core_CookiePrivacyPolicy")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.confirmGridItem}>
                    <Button
                        {...styles.acceptButton}
                        onClick={acceptHandler}
                        data-test-selector="cookiePolicy_AcceptCookies"
                    >
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                    <Button
                        {...styles.closeButton}
                        onClick={closeHandler}
                        data-test-selector="cookiePolicy_ClosePolicy"
                        aria-label={translate("Close")}
                    >
                        {translate("Decline")}
                    </Button>
                </GridItem>
            </GridContainer>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
